/* eslint-disable */
import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tag, Table, Tooltip, Button, Select, Switch, Input } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faSave, faTrash, faX } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";
import { RootState } from "../../../../app/store";
import {
  DirectionType,
  DoorType,
  ConditionDef,
  RuleDefinition,
  FlatRowInterface,
  RuleExpr,
  CatalogItem,
} from "../../../../interfaces/IconDefinitions";
import { directionDisplayName } from "../../../../flatbuffers/types/enumDisplayUtils";
import { LiftType } from "../../../../interfaces/Lift.interface";
// FLATBUFFERS SCHEMAS
import { Traffic } from "../../../../flatbuffers/generated/traffic/traffic";
import { Usage } from "../../../../flatbuffers/generated/usage/usage";
import { Service } from "../../../../flatbuffers/generated/service/service";
import { Control } from "../../../../flatbuffers/generated/control/control";
import { Fault } from "../../../../flatbuffers/generated/fault/fault";
import { GroupSupervisoryStatus } from "../../../../flatbuffers/generated/group-supervisory-status/group-supervisory-status";
import { GroupControl } from "../../../../flatbuffers/generated/group-control/group-control";
import { ControllerInfoContent } from "../../../../flatbuffers/generated/controller-info/controller-info-content";
import { Status } from "../../../../flatbuffers/generated/status/status";
// CUSTOM HOOKS
import usePutAdapter from "../../../../customHooks/PUT_requests/usePutAdapter";
// COMPONENTS
import TableColumnLiftType from "../../../components/table/TableColumnLiftType";
import SelectIconElevator from "../../../components/select/SelectIconElevator";
import SelectIconEscalator from "../../../components/select/SelectIconEscalator";
import IconDefinitionMessagesCatalog from "./IconDefinitionMessagesCatalog";
import IconsGetIconByName from "../../../components/icons/IconsGetIconByName";
// ICONS
import elDown from "../../../../assets/icons/elevatorStatus/el-down.svg";
import elUp from "../../../../assets/icons/elevatorStatus/el-up.svg";
// CONSTANTS
import {
  DEFAULT_CONDITION,
  DIRECTION_TYPES,
  MESSAGE_TYPE_OPTIONS,
  DEFAULT_ELEVATOR_RULES_OBJECT,
} from "../../../../constants/iconDefinition";
// UTILS
import { getTableRowClass } from "../../../../utils/utils";
import { EncodedMessageContent } from "../../../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../../../../flatbuffers/generated/ln2/message-content";

type AnyObj = { [key: string]: any };

export default function IconDefinitionConditionsTable({
  adapter,
  isLoading,
}: {
  isLoading: boolean;
  adapter: AdapterInterface;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Column, ColumnGroup } = Table;
  const [t] = useTranslation("global");
  const { Option } = Select;
  const enumOptions = (enumObj: any): CatalogItem[] =>
    Object.entries(enumObj)
      .filter(([, v]) => typeof v === "number")
      .map(([label, value]: any) => ({ label, value }));

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });
  const [condition, setCondition] = useState<ConditionDef>({
    ...DEFAULT_CONDITION,
  });
  const [icon, setIcon] = useState<string>("elUp");
  const [name, setName] = useState<string>("");
  const [priorityRules, setPriorityRules] = useState<RuleDefinition[]>(
    DEFAULT_ELEVATOR_RULES_OBJECT,
  );
  const [tableData, setTableData] = useState<FlatRowInterface[]>([]);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { putAdapter } = usePutAdapter();

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeIcon = (newValue: string) => {
    setIcon(newValue);
  };

  const onChangeContent = (
    newContentType: EncodedMessageContent | null,
    newMessageType: MessageContent | MessageContent,
  ) => {
    setCondition((prev: ConditionDef) => ({
      ...prev,
      condition: {
        ...prev.condition,
        contentType: newContentType,
        messageType: newMessageType,
      },
    }));
  };

  const onChangeMessage = (newValue: number) => {
    setCondition((prev: ConditionDef) => ({
      ...prev,
      condition: { ...prev.condition, message: newValue },
    }));
  };

  const inputOnChangeName = (newValue: string) => {
    setName(newValue);
  };

  const onChangeActive = (newValue: boolean) => {
    setCondition((prev: ConditionDef) => ({
      ...prev,
      condition: { ...prev.condition, active: newValue },
    }));
  };

  const onChangeDirection = (newValue: DirectionType | null) => {
    setCondition((prev: ConditionDef) => ({
      ...prev,
      condition: { ...prev.condition, direction: newValue },
    }));
  };

  const onChangeSwitchFrontDoor = (newValue: DoorType, type: "front" | "rear") => {
    if (type === "front") {
      setCondition((prev: ConditionDef) => ({
        ...prev,
        condition: { ...prev.condition, frontDoor: newValue },
      }));
    } else if (type === "rear") {
      setCondition((prev: ConditionDef) => ({
        ...prev,
        condition: { ...prev.condition, rearDoor: newValue },
      }));
    }
  };

  const socketMsgPriority: { customConfiguration?: boolean; rules?: RuleDefinition[] } = (() => {
    try {
      const raw = adapter?.socketMessagePriorityJson;
      if (!raw) return {};
      return typeof raw === "string" ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })();

  const onClickSave = () => {
    const index = priorityRules.findIndex((item: RuleDefinition) => item.icon === icon);
    const copy: RuleDefinition[] = structuredClone(priorityRules);
    copy[index].conditions.push({
      id: crypto.randomUUID(),
      name,
      condition: condition.condition,
    });
    if (index >= 0) {
      putAdapter({
        ...adapter,
        socketMessagePriorityJson: JSON.stringify({
          // Spread first. This handler used to rebuild the blob from the two
          // fields it knows about, so saving a condition silently dropped every
          // other field in it — displayMapMode, iconStalenessTimeout, and now
          // hallCallBindings. Its sibling handlers all spread; this one was the
          // odd one out.
          ...socketMsgPriority,
          customConfiguration: socketMsgPriority?.customConfiguration,
          rules: copy,
        }),
      });
    }
  };

  const handleTableChange = async (pagination_: any) => {
    setPagination({
      ...pagination,
      current: pagination_.current,
      pageSize: pagination_.pageSize,
      total: pagination_.total,
    });
  };

  const onClickCancel = () => {
    setCondition({ ...DEFAULT_CONDITION });
    setIcon(adapter?.liftType === LiftType.ESCALATOR ? "esUp" : "elUp");
  };

  const onClickDelete = (row: FlatRowInterface) => {
    const updatedRules: RuleDefinition[] = priorityRules.map((r) => {
      if (r.id !== row.ruleId) return r;

      return {
        ...r,
        conditions: (r.conditions ?? []).filter((c) => c.id !== row.id),
      };
    });

    putAdapter({
      ...adapter,
      socketMessagePriorityJson: JSON.stringify({
        ...socketMsgPriority,
        rules: updatedRules,
      }),
    });
  };

  const onChangeSwitchCustomConfiguration = (newValue: boolean) => {
    putAdapter({
      ...adapter,
      socketMessagePriorityJson: JSON.stringify({
        ...socketMsgPriority,
        customConfiguration: newValue,
        // Persist what is STORED, not the in-memory `priorityRules`.
        //
        // `priorityRules` is seeded from DEFAULT_ELEVATOR_RULES_OBJECT and is
        // only replaced when the adapter already has rules, so on an adapter
        // with none this used to write all 15 defaults into it as a side effect
        // of flipping an unrelated switch. That was invisible while those
        // defaults were semantically empty; now that usageCond builds real
        // message identities they would actually match, and toggling this
        // switch would silently change what the site renders.
        //
        // A toggle should persist its own field and leave the rest alone. Real
        // rule edits go through onClickDelete/onClickSave, which write
        // deliberately.
        rules: socketMsgPriority?.rules ?? [],
      }),
    });
  };

  const normalizeStr = (v: any) => (typeof v === "string" ? v.trim().toLowerCase() : v);

  const normalizeValue = (v: any) => {
    if (v === undefined) return null;
    if (typeof v === "string") return v.trim();
    return v;
  };

  const stableNormalizeObject = (obj: AnyObj): AnyObj => {
    if (obj === null || obj === undefined) return obj;

    if (Array.isArray(obj)) {
      return obj.map(stableNormalizeObject);
    }

    if (typeof obj === "object") {
      const sortedKeys = Object.keys(obj).sort();
      const out: AnyObj = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const k of sortedKeys) {
        out[k] = stableNormalizeObject(normalizeValue(obj[k]));
      }
      return out;
    }

    return normalizeValue(obj);
  };

  const deepEqualNormalized = (a: any, b: any) => {
    const na = stableNormalizeObject(a);
    const nb = stableNormalizeObject(b);
    return JSON.stringify(na) === JSON.stringify(nb);
  };

  const disableSaveButton = (): boolean => {
    const trimmedName = (name ?? "").trim();
    const trimmedIcon = (icon ?? "").trim();
    if (!trimmedIcon) return true;

    if (!trimmedName) return true;

    if (!condition?.condition) return true;

    const newCondNameKey = normalizeStr(trimmedName);
    const newCondPayload = condition.condition;

    const allExistingConditions = priorityRules?.flatMap((r: any) => r?.conditions ?? []) ?? [];

    const hasDuplicate = allExistingConditions.some((c: any) => {
      const existingNameKey = normalizeStr(c?.name ?? "");
      const sameName = !!newCondNameKey && existingNameKey === newCondNameKey;

      const sameValues = deepEqualNormalized(c?.condition, newCondPayload);

      return sameName || sameValues;
    });

    return hasDuplicate;
  };

  const disableDeleteButton = (item: FlatRowInterface): boolean => {
    const conditionId = item.id;
    const rule = priorityRules.find((r) => r.id === item.ruleId);
    if (!rule) return false;
    const containsRefId = (expr: RuleExpr | undefined, targetId: string): boolean => {
      if (!expr) return false;
      if (expr.kind === "ref") {
        return expr.id === targetId;
      }
      return expr.children.some((child) => containsRefId(child, targetId));
    };

    return containsRefId(rule.activeWhen, conditionId);
  };

  const getMessageLabel = (
    liftType: LiftType,
    contentType: EncodedMessageContent | null,
    messageType: MessageContent | MessageContent | null,
    messageValue: number | null,
  ): string => {
    if (messageValue == null || contentType == null || messageType == null) return "--";

    let catalog: CatalogItem[] = [];

    if (contentType === EncodedMessageContent.OneHotMessage) {
      if (messageType === MessageContent.Usage_UsageMessage) {
        catalog = enumOptions(liftType === LiftType.ELEVATOR ? Usage : Usage);
      } else if (messageType === MessageContent.Service_ServiceMessage) {
        catalog = enumOptions(liftType === LiftType.ELEVATOR ? Service : Service);
      }
    }

    if (contentType === EncodedMessageContent.BitMaskMessage) {
      if (messageType === MessageContent.Traffic_TrafficMessage) {
        catalog = enumOptions(liftType === LiftType.ELEVATOR ? Traffic : Traffic);
      } else if (messageType === MessageContent.Status_StatusMessage) {
        catalog = enumOptions(Status);
      } else if (messageType === MessageContent.Control_ControlMessage) {
        catalog = enumOptions(liftType === LiftType.ELEVATOR ? Control : Control);
      } else if (messageType === MessageContent.Fault_FaultMessage) {
        catalog = enumOptions(liftType === LiftType.ELEVATOR ? Fault : Fault);
      } else if (
        messageType === MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage
      ) {
        catalog = enumOptions(
          liftType === LiftType.ELEVATOR ? GroupSupervisoryStatus : GroupSupervisoryStatus,
        );
      } else if (messageType === MessageContent.GroupControl_GroupControlMessage) {
        catalog = enumOptions(liftType === LiftType.ELEVATOR ? GroupControl : GroupControl);
      } else if (messageType === MessageContent.ControllerInfo_ControllerInfoMessage) {
        catalog = enumOptions(
          liftType === LiftType.ELEVATOR ? ControllerInfoContent : ControllerInfoContent,
        );
      }
    }

    return catalog.find((x) => x.value === messageValue)?.label ?? String(messageValue);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (adapter?.liftType === LiftType.ESCALATOR) {
      setIcon("esUp");
    }
  }, [adapter]);

  useEffect(() => {
    if (socketMsgPriority?.rules && socketMsgPriority.rules.length > 0) {
      setPriorityRules(socketMsgPriority.rules);
      const rows: FlatRowInterface[] = socketMsgPriority.rules.flatMap((rule: RuleDefinition) =>
        (rule.conditions ?? []).map((c) => ({
          icon: rule.icon?.trim(),
          id: c.id,
          name: c.name,
          condition: c.condition,
          ruleId: rule.id,
        })),
      );
      setTableData(rows);
    }
  }, [adapter]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div
        className="generalStyles__pageHeader"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          <div className="generalStyles__flex">
            <TableColumnLiftType liftType={adapter.liftType} />
            <Tag color="orange">{adapter.protocolType}</Tag>
            <Tag color="cyan">{adapter.name || "--"}</Tag>
            <Tag color="geekblue">ICON DEFINITION</Tag>
          </div>
        </div>
        <div className="generalStyles__flex ">
          <Switch
            onChange={(e) => {
              onChangeSwitchCustomConfiguration(e);
            }}
            checked={socketMsgPriority?.customConfiguration}
            style={{ width: "100px" }}
            checkedChildren="CUSTOM"
            unCheckedChildren="DEFAULT"
          />
        </div>
      </div>
      <Table
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={tableData}
        pagination={{ ...pagination, total: priorityRules.length || 0 }}
        onChange={handleTableChange}
        size="small"
        className="mt-3"
        loading={isLoading}
        scroll={{ x: 900 }}
        footer={() => (
          <div className="generalStyles__flexEnd">
            <Tag>{priorityRules.length || 0} Rows</Tag>
          </div>
        )}
      >
        {/** ICON */}
        <ColumnGroup title={t("general.icon")}>
          <Column
            title={
              <>
                {adapter.liftType === LiftType.ELEVATOR && (
                  <SelectIconElevator setValue={onChangeIcon} value={icon} />
                )}
                {adapter.liftType === LiftType.ESCALATOR && (
                  <SelectIconEscalator setValue={onChangeIcon} value={icon} />
                )}
              </>
            }
            dataIndex="icon"
            key="icon"
            render={(text) => (
              <IconsGetIconByName name={text} liftType={adapter.liftType || LiftType.ELEVATOR} />
            )}
          />
        </ColumnGroup>
        {/** NAME */}
        <ColumnGroup title={t("general.name")}>
          <Column
            width={200}
            title={
              <Input
                className="generalStyles__input"
                size="small"
                value={name}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  inputOnChangeName(event.currentTarget.value);
                }}
              />
            }
            dataIndex="name"
            key="name"
            render={(text) => (
              <div>
                <FontAwesomeIcon
                  icon={faCircle}
                  className={`${text === "Disabled" ? "" : "generalStyles__success"}`}
                  style={{ marginRight: "10px" }}
                />
                <span>{text}</span>
              </div>
            )}
          />
        </ColumnGroup>
        {/** MESSAGE TYPE */}
        <ColumnGroup title={t("general.type")}>
          <Column
            width={200}
            title={
              <Select
                optionFilterProp="children"
                className="generalStyles__width100"
                allowClear={false}
                size="small"
                value={
                  condition.condition.contentType && condition.condition.messageType != null
                    ? `${condition.condition.contentType}:${condition.condition.messageType}`
                    : undefined
                }
                onChange={(v: string) => {
                  const [ctRaw, mtRaw] = v.split(":");
                  const contentType = Number(ctRaw) as EncodedMessageContent;
                  const messageType = Number(mtRaw) as MessageContent | MessageContent;
                  onChangeContent(contentType, messageType);
                }}
              >
                {MESSAGE_TYPE_OPTIONS.map((opt) => (
                  <Option
                    key={`${opt.contentType}:${opt.messageType}`}
                    value={`${opt.contentType}:${opt.messageType}`}
                  >
                    <FontAwesomeIcon
                      className="generalStyles__success"
                      icon={faCircle}
                      style={{ marginRight: "10px" }}
                    />
                    {opt.label}
                  </Option>
                ))}
              </Select>
            }
            dataIndex={["condition", "type"]}
            key="messageType"
            render={(_, row: any) => {
              const ct = row?.condition?.contentType as EncodedMessageContent | null;
              const mt = row?.condition?.messageType as MessageContent | MessageContent | null;
              const mv = row?.condition?.message as number | null;
              const label = getMessageLabel(adapter.liftType, ct, mt, mv);
              return (
                <div>
                  <FontAwesomeIcon
                    icon={faCircle}
                    className="generalStyles__success"
                    style={{ marginRight: "10px" }}
                  />
                  <span>{label}</span>
                </div>
              );
            }}
          />
        </ColumnGroup>
        {/** MESSAGE */}
        <ColumnGroup title={t("general.message")}>
          <Column
            width={200}
            title={
              <IconDefinitionMessagesCatalog
                message={condition.condition.message ?? null}
                onChangeMessage={onChangeMessage}
                liftType={adapter.liftType}
                contentType={condition.condition.contentType}
                messageType={condition.condition.messageType}
                disabled={false}
              />
            }
            dataIndex={["condition", "message"]}
            key="message"
            render={(_, row: any) => {
              const ct = row?.condition?.contentType as EncodedMessageContent | null;
              const mt = row?.condition?.messageType as MessageContent | MessageContent | null;
              const opt = MESSAGE_TYPE_OPTIONS.find(
                (x) => x.contentType === ct && x.messageType === mt,
              );
              return (
                <div>
                  <FontAwesomeIcon
                    icon={faCircle}
                    className="generalStyles__success"
                    style={{ marginRight: "10px" }}
                  />
                  <span>{opt?.label ?? "--"}</span>
                </div>
              );
            }}
          />
        </ColumnGroup>
        {/** ACTIVE */}
        <ColumnGroup title={t("general.active")}>
          <Column
            width={200}
            title={
              <Select
                optionFilterProp="children"
                className="generalStyles__width100"
                allowClear={false}
                size="small"
                value={condition.condition.active}
                onChange={(e) => {
                  onChangeActive(e);
                }}
              >
                {[
                  { id: "true", name: "True", value: true },
                  { id: "false", name: "False", value: false },
                ].map((item: { id: string; name: string; value: boolean }) => (
                  <Option key={item.id} value={item.value}>
                    <FontAwesomeIcon
                      icon={faCircle}
                      className={item.value ? "generalStyles__success" : "generalStyles__error"}
                      style={{ marginRight: "10px" }}
                    />
                    {item.name}
                  </Option>
                ))}
              </Select>
            }
            dataIndex={["condition", "active"]}
            key="active"
            render={(text) => (
              <div>
                <FontAwesomeIcon
                  icon={faCircle}
                  className={text ? "generalStyles__success" : "generalStyles__error"}
                  style={{ marginRight: "10px" }}
                />
                <span>{text ? "True" : "False"}</span>
              </div>
            )}
          />
        </ColumnGroup>
        {/** DIRECTION */}
        <ColumnGroup title={t("general.direction")}>
          <Column
            width={200}
            title={
              <Select
                optionFilterProp="children"
                className="generalStyles__width100"
                allowClear={false}
                size="small"
                value={condition.condition.direction}
                onChange={(e: DirectionType) => {
                  onChangeDirection(e);
                }}
              >
                {DIRECTION_TYPES.map((item: DirectionType) => (
                  <Option key={String(item)} value={item}>
                    {item === null ? (
                      <FontAwesomeIcon icon={faCircle} style={{ marginRight: "10px" }} />
                    ) : (
                      <>
                        {item === Usage.Up ? (
                          <img src={elUp} alt="Up" />
                        ) : (
                          <img src={elDown} alt="Down" />
                        )}
                      </>
                    )}
                    {item !== null ? directionDisplayName(item) : "Ignore"}
                  </Option>
                ))}
              </Select>
            }
            dataIndex={["condition", "direction"]}
            key="direction"
            render={(text) => (
              <div>
                {text === Usage.Up && (
                  <span>
                    <img src={elUp} alt="Up" /> Up
                  </span>
                )}
                {text === Usage.Down && (
                  <span>
                    {" "}
                    <img src={elDown} alt="Down" /> Down
                  </span>
                )}
                {text === null && (
                  <span>
                    <FontAwesomeIcon icon={faCircle} style={{ marginRight: "10px" }} /> Ignore
                  </span>
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** FRONT DOOR */}
        {adapter?.liftType === LiftType.ELEVATOR && (
          <ColumnGroup title={t("general.frontDoor")}>
            <Column
              width={200}
              title={
                <Select
                  optionFilterProp="children"
                  className="generalStyles__width100"
                  allowClear={false}
                  size="small"
                  value={condition.condition.frontDoor}
                  onChange={(e) => {
                    onChangeSwitchFrontDoor(e, "front");
                  }}
                >
                  {[
                    { value: null, name: "Ignore" },
                    { value: true, name: "Open/True" },
                    { value: false, name: "Close/False" },
                  ].map((item: { value: DoorType; name: string }) => (
                    <Option key={item.name} value={item.value}>
                      <FontAwesomeIcon
                        className={
                          item.value === null
                            ? ""
                            : `${item.value ? "generalStyles__success" : "generalStyles__error"}`
                        }
                        icon={faCircle}
                        style={{ marginRight: "10px" }}
                      />
                      {item.name}
                    </Option>
                  ))}
                </Select>
              }
              dataIndex={["condition", "frontDoor"]}
              key="frontDoor"
              render={(text) => (
                <div>
                  <FontAwesomeIcon
                    icon={faCircle}
                    className={
                      text === null
                        ? ""
                        : `${text ? "generalStyles__success" : "generalStyles__error"}`
                    }
                    style={{ marginRight: "10px" }}
                  />
                  <span>{text === null ? "Ignore" : `${text ? "Open/True" : "Close/False"}`}</span>
                </div>
              )}
            />
          </ColumnGroup>
        )}
        {/** REAR DOOR */}
        {adapter?.liftType === LiftType.ELEVATOR && (
          <ColumnGroup title={t("general.rearDoor")}>
            <Column
              width={200}
              title={
                <Select
                  optionFilterProp="children"
                  className="generalStyles__width100"
                  allowClear={false}
                  size="small"
                  value={condition.condition.rearDoor}
                  onChange={(e) => {
                    onChangeSwitchFrontDoor(e, "rear");
                  }}
                >
                  {[
                    { value: null, name: "Ignore" },
                    { value: true, name: "Open/True" },
                    { value: false, name: "Close/False" },
                  ].map((item: { value: DoorType; name: string }) => (
                    <Option key={item.name} value={item.value}>
                      <FontAwesomeIcon
                        className={
                          item.value === null
                            ? ""
                            : `${item.value ? "generalStyles__success" : "generalStyles__error"}`
                        }
                        icon={faCircle}
                        style={{ marginRight: "10px" }}
                      />
                      {item.name}
                    </Option>
                  ))}
                </Select>
              }
              dataIndex={["condition", "rearDoor"]}
              key="rearDoor"
              render={(text) => (
                <div>
                  <FontAwesomeIcon
                    icon={faCircle}
                    className={
                      text === null
                        ? ""
                        : `${text ? "generalStyles__success" : "generalStyles__error"}`
                    }
                    style={{ marginRight: "10px" }}
                  />
                  <span>{text === null ? "Ignore" : `${text ? "Open/True" : "Close/False"}`}</span>
                </div>
              )}
            />
          </ColumnGroup>
        )}
        {/** ACTIONS */}
        <ColumnGroup align="center" title={t("edgeManagement.banks.actions")}>
          <Column
            width={200}
            align="center"
            title={
              <div className="generalStyles__flexCenter">
                <div>
                  <Tooltip
                    title={
                      disableSaveButton() ? t("general.errorIconCondition") : t("general.save")
                    }
                  >
                    <Button
                      className={disableSaveButton() ? "" : "buttonStyle__10"}
                      onClick={onClickSave}
                      icon={<FontAwesomeIcon icon={faSave} />}
                      disabled={disableSaveButton()}
                    />
                  </Tooltip>
                </div>
                <div className="generalStyles__mlFix">
                  <Tooltip title={t("general.clear")}>
                    <Button
                      onClick={onClickCancel}
                      className="buttonStyle__14"
                      icon={<FontAwesomeIcon icon={faX} />}
                    />
                  </Tooltip>
                </div>
              </div>
            }
            key="action"
            render={(item) => (
              <div>
                <Tooltip
                  title={`${
                    disableDeleteButton(item)
                      ? t("general.deleteRuleIconCondition")
                      : t("general.delete")
                  }`}
                >
                  <Button
                    shape="circle"
                    className={`${disableDeleteButton(item) ? "" : "buttonStyle__29"}`}
                    onClick={() => {
                      onClickDelete(item);
                    }}
                    disabled={disableDeleteButton(item)}
                    icon={<FontAwesomeIcon style={{ fontSize: "15px" }} icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            )}
          />
        </ColumnGroup>
      </Table>
    </div>
  );
}
